import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import * as _ from 'lodash';

import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UrlFieldValidators } from 'src/app/components/url-field/url-field-validators';
import { PresentationsService } from 'src/app/presentations/services/presentations.service';
import { PlaylistService } from '../../services/playlist.service';

@Component({
  selector: 'playlist-item',
  templateUrl: './playlist-item.component.html',
  styleUrls: ['./playlist-item.component.scss'],
  standalone: false,
})
export class PlaylistItemComponent implements OnInit {
  transitionTypes = PlaylistService.TRANSITION_TYPES;
  companyId = this.companyStateService.getSelectedCompanyId();

  playlistItem: any = null;
  _playlistItem;

  isNew;
  playUntilDoneSupported;
  loadingTemplate;

  playlistItemForm: FormGroup<PlaylistItemForm>;

  constructor(
    private companyStateService: CompanyStateService,
    private playlistService: PlaylistService,
    private presentationsService: PresentationsService,
    private httpClient: HttpClient,
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<PlaylistItemComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.playlistItem = data.playlistItem;
    this.setupForm();
  }

  ngOnInit(): void {
    if (this.playlistItem.type === 'url') {
      this.addUrlControlField();
    }
    this._playlistItem = _.cloneDeep(this.playlistItem);
    this.isNew = this.playlistService.isNew(this.playlistItem);

    const transitionType =
      this._playlistItem.transitionType ??
      PlaylistService.TRANSITION_TYPES[0][0];
    this._playlistItem.transitionType = transitionType;
    this.playlistItemForm.controls.transitionType.setValue(transitionType);

    this.configurePlayUntilDone();
  }

  selectPresentation() {
    this.presentationsService
      .openPresentationSelector()
      .then((presentationDetails) => {
        this._playlistItem.objectReference = presentationDetails[0];
        this._playlistItem.presentationType = presentationDetails[2];
        this.configurePlayUntilDone();
      });
  }

  configurePlayUntilDone() {
    if (this._playlistItem.type === 'url') {
      return;
    }

    this.playUntilDoneSupported = true;
    this.loadingTemplate = true;

    this.presentationsService
      .getPresentationCached(this._playlistItem.objectReference)
      .then((presentation) => {
        return this.playlistService.initPlayUntilDone(
          this._playlistItem,
          presentation,
          this.isNew,
        );
      })
      .then((playUntilDone) => {
        this.playUntilDoneSupported = playUntilDone;
      })
      .finally(() => {
        this.loadingTemplate = false;
      });
  }

  protected removeValidationChange(removeValidations: boolean) {
    const validators: ValidatorFn[] = [
      Validators.required,
      UrlFieldValidators.noPreviewUrl,
      UrlFieldValidators.http,
    ];
    this.playlistItemForm.get('objectReference').clearValidators();
    this.playlistItemForm.get('objectReference').clearAsyncValidators();

    if (removeValidations) {
      this.playlistItemForm.get('objectReference').setAsyncValidators(
        UrlFieldValidators.responseHeader(this.httpClient, {
          headersToIgnore: ['not-reachable'],
        }),
      );
    } else {
      validators.push(UrlFieldValidators.url);
      this.playlistItemForm
        .get('objectReference')
        .setAsyncValidators(UrlFieldValidators.responseHeader(this.httpClient));
    }
    this.playlistItemForm
      .get('objectReference')
      .addValidators(Validators.compose(validators));
    this.playlistItemForm.get('objectReference').updateValueAndValidity();
  }

  private addUrlControlField() {
    this.playlistItemForm.addControl(
      'objectReference',
      this.formBuilder.control(
        '',
        Validators.compose([
          Validators.required,
          UrlFieldValidators.url,
          UrlFieldValidators.http,
          UrlFieldValidators.noPreviewUrl,
        ]),
        UrlFieldValidators.responseHeader(this.httpClient),
      ),
    );
    this.playlistItemForm.updateValueAndValidity();
  }

  private setupForm() {
    this.playlistItemForm = this.formBuilder.group<PlaylistItemForm>({
      itemName: this.formBuilder.nonNullable.control('', Validators.required),
      playUntilDone: this.formBuilder.control(false),
      duration: this.formBuilder.control(0, Validators.required),
      transitionType: this.formBuilder.control(null),
    });
  }

  saveDisabled() {
    return (
      (this.f['duration'] && this.f['duration'].invalid) ||
      (!!this.f['objectReference'] &&
        this.f['objectReference'].invalid &&
        (this.f['objectReference'].dirty || this.isNew))
    );
  }

  save() {
    if (this.saveDisabled()) {
      console.error('form not valid');
      return;
    }
    Object.assign(this.playlistItem, {
      ...this._playlistItem,
      ...this.playlistItemForm.value,
    });
    this.playlistService.updatePlaylistItem(this.playlistItem);
    this.dialogRef.close(true);
  }

  dismiss() {
    this.dialogRef.close(false);
  }

  protected get f() {
    return this.playlistItemForm.controls;
  }
}

type PlaylistItemForm = {
  itemName: FormControl<string>;
  objectReference?: FormControl<string>;
  playUntilDone: FormControl<boolean>;
  duration: FormControl<number>;
  transitionType: FormControl<string>;
};
